import { supabase } from '@/lib/supabase';
import type {
  DashboardChatActionInput,
  DashboardChatSendAction,
  DashboardChatMessage,
  DashboardChatResponse,
  DashboardChatResult,
} from '@shared/types/dashboard-chat';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDashboardChatResult = (value: unknown): value is DashboardChatResult => {
  if (!isRecord(value) || typeof value.type !== 'string') return false;

  if (value.type === 'text_answer') {
    return value.intent === 'question_simple' && typeof value.answer === 'string';
  }

  if (value.type === 'sourced_answer') {
    return (
      value.intent === 'work_answer' &&
      typeof value.answer === 'string' &&
      Array.isArray(value.citations) &&
      Array.isArray(value.unread)
    );
  }

  if (value.type === 'gesture_result') {
    return value.intent === 'gestures' && typeof value.answer === 'string' && Array.isArray(value.lines) && Array.isArray(value.undo);
  }

  if (value.type === 'app_record_proposal') {
    return (
      value.intent === 'app_record' &&
      typeof value.answer === 'string' &&
      typeof value.appId === 'string' &&
      typeof value.recordId === 'string' &&
      typeof value.expectedVersion === 'number' &&
      Array.isArray(value.edits) &&
      typeof value.rerun === 'boolean' &&
      Array.isArray(value.sources)
    );
  }

  if (value.type === 'launch_proposal') {
    return (
      value.intent === 'app_launch' &&
      typeof value.answer === 'string' &&
      typeof value.flowId === 'string' &&
      typeof value.appKey === 'string' &&
      typeof value.appTitle === 'string' &&
      Array.isArray(value.inputs) &&
      typeof value.sourceText === 'string'
    );
  }

  if (value.type === 'import_proposal') {
    return (
      value.intent === 'table_import' &&
      typeof value.answer === 'string' &&
      (value.appKey === null || typeof value.appKey === 'string') &&
      (value.rows === null || typeof value.rows === 'number') &&
      typeof value.sourceText === 'string'
    );
  }

  if (value.type === 'ad_hoc_result') {
    return (
      value.intent === 'question_ad_hoc' &&
      typeof value.answer === 'string' &&
      isRecord(value.resolutionPlan)
    );
  }

  if (value.type === 'existing_flow') {
    return (
      value.intent === 'reuse_existing_flow' &&
      typeof value.answer === 'string' &&
      typeof value.flowId === 'string' &&
      typeof value.flowTitle === 'string' &&
      isRecord(value.resolutionPlan)
    );
  }

  if (value.type === 'template_handoff') {
    return (
      value.intent === 'template_install' &&
      typeof value.answer === 'string' &&
      typeof value.templateKey === 'string' &&
      typeof value.templateTitle === 'string' &&
      isRecord(value.resolutionPlan)
    );
  }

  if (value.type === 'prebuilt_tool_handoff') {
    return (
      value.intent === 'prebuilt_tool_open' &&
      typeof value.answer === 'string' &&
      typeof value.toolKey === 'string' &&
      typeof value.toolTitle === 'string' &&
      isRecord(value.resolutionPlan)
    );
  }

  if (value.type === 'flow_handoff') {
    return (
      value.intent === 'flow_create' &&
      isRecord(value.seed) &&
      isRecord(value.resolutionPlan)
    );
  }

  if (value.type === 'action_proposal') {
    return (
      value.intent === 'contextual_action' &&
      typeof value.answer === 'string' &&
      isRecord(value.action) &&
      typeof value.action.type === 'string'
    );
  }

  if (value.type === 'navigation_handoff') {
    return (
      value.intent === 'navigation_open' &&
      typeof value.answer === 'string' &&
      typeof value.title === 'string' &&
      typeof value.href === 'string' &&
      typeof value.actionLabel === 'string'
    );
  }

  if (value.type === 'thread_replacement_proposal') {
    const proposal = value.proposal;
    const sourceDecision = value.sourceDecision;
    return (
      value.intent === 'thread_decision_replacement' &&
      typeof value.answer === 'string' &&
      isRecord(sourceDecision) &&
      typeof sourceDecision.topicTitle === 'string' &&
      (sourceDecision.reason === null || typeof sourceDecision.reason === 'string') &&
      isRecord(proposal) &&
      typeof proposal.id === 'string' &&
      typeof proposal.threadId === 'string' &&
      typeof proposal.topicId === 'string' &&
      typeof proposal.sourceDecisionId === 'string' &&
      (proposal.sourceActionId === null || typeof proposal.sourceActionId === 'string') &&
      typeof proposal.sourceTopicVersion === 'number' &&
      typeof proposal.replacementGoal === 'string' &&
      typeof proposal.reason === 'string' &&
      Array.isArray(proposal.evidenceRefs) &&
      proposal.evidenceRefs.every((ref) => typeof ref === 'string') &&
      ['pending', 'confirmed', 'rejected', 'expired'].includes(String(proposal.status)) &&
      typeof proposal.expiresAt === 'string' &&
      (proposal.replacementDecisionId === null ||
        typeof proposal.replacementDecisionId === 'string') &&
      (proposal.evaluationJobId === null ||
        typeof proposal.evaluationJobId === 'string') &&
      (proposal.resolvedAt === null || typeof proposal.resolvedAt === 'string')
    );
  }

  if (value.type === 'thread_wait_proposal') {
    const proposal = value.proposal;
    const sourceDecision = value.sourceDecision;
    return (
      value.intent === 'thread_wait_management' &&
      typeof value.answer === 'string' &&
      isRecord(sourceDecision) &&
      typeof sourceDecision.topicTitle === 'string' &&
      (sourceDecision.reason === null || typeof sourceDecision.reason === 'string') &&
      isRecord(proposal) &&
      typeof proposal.id === 'string' &&
      typeof proposal.threadId === 'string' &&
      typeof proposal.topicId === 'string' &&
      typeof proposal.sourceDecisionId === 'string' &&
      (proposal.sourceActionId === null || typeof proposal.sourceActionId === 'string') &&
      typeof proposal.sourceTopicVersion === 'number' &&
      (proposal.operation === 'create' || proposal.operation === 'reschedule') &&
      Array.isArray(proposal.options) &&
      proposal.options.every(
        (option) =>
          isRecord(option) &&
          ['time', 'gmail', 'google_calendar', 'hubspot'].includes(String(option.mode)) &&
          ['exact', 'reminder_only'].includes(String(option.coverage)) &&
          typeof option.label === 'string' &&
          typeof option.description === 'string' &&
          (option.maximumTargetAt === null ||
            typeof option.maximumTargetAt === 'string') &&
          (option.suggestedTargetAt === null ||
            typeof option.suggestedTargetAt === 'string')
      ) &&
      ['time', 'gmail', 'google_calendar', 'hubspot'].includes(
        String(proposal.selectedMode)
      ) &&
      typeof proposal.targetAt === 'string' &&
      typeof proposal.timezone === 'string' &&
      typeof proposal.reason === 'string' &&
      ['pending', 'confirmed', 'rejected', 'expired'].includes(String(proposal.status)) &&
      typeof proposal.expiresAt === 'string' &&
      (proposal.confirmedWaitId === null ||
        typeof proposal.confirmedWaitId === 'string') &&
      (proposal.resolvedAt === null || typeof proposal.resolvedAt === 'string')
    );
  }

  return false;
};

const isDashboardChatMessage = (value: unknown): value is DashboardChatMessage =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  (value.role === 'user' || value.role === 'assistant') &&
  typeof value.content === 'string' &&
  typeof value.createdAt === 'string' &&
  (value.result === undefined || isDashboardChatResult(value.result));

const isDashboardChatResponse = (value: unknown): value is DashboardChatResponse =>
  isRecord(value) &&
  (value.status === 'ready' || value.status === 'answered') &&
  typeof value.conversationId === 'string' &&
  Array.isArray(value.messages) &&
  value.messages.every((entry) => isDashboardChatMessage(entry));

const dashboardChatWorkerUrl = import.meta.env.VITE_DASHBOARD_CHAT_WORKER_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const getDashboardChatWorkerUrl = (): string => {
  if (!dashboardChatWorkerUrl) {
    throw new Error(
      'Dashboard chat worker non configuré: définissez VITE_DASHBOARD_CHAT_WORKER_URL.'
    );
  }

  return dashboardChatWorkerUrl;
};

const getAccessToken = async (): Promise<string> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('Session Supabase manquante pour le chat dashboard.');
  }

  return accessToken;
};

const toDashboardChatError = async (response: Response): Promise<Error> => {
  const bodyText = await response.text();

  try {
    const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
    const message = parsed.message ?? parsed.error;
    if (typeof message === 'string' && message.length > 0) {
      return new Error(message);
    }
  } catch {
    // Ignore JSON parse failures.
  }

  return new Error(bodyText || 'Impossible d’utiliser le chat dashboard pour le moment.');
};

export const invokeDashboardChat = async (
  input: DashboardChatActionInput
): Promise<DashboardChatResponse> => {
  const accessToken = await getAccessToken();
  const response = await fetch(getDashboardChatWorkerUrl(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      apikey: supabaseAnonKey,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await toDashboardChatError(response);
  }

  const data = (await response.json()) as unknown;
  if (!isDashboardChatResponse(data)) {
    throw new Error('Réponse invalide du chat dashboard.');
  }

  return data;
};

const parseSseEvent = (rawEvent: string): { event: string; data: unknown } | null => {
  const lines = rawEvent
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return null;
  }

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  try {
    return {
      event,
      data: JSON.parse(dataLines.join('\n')) as unknown,
    };
  } catch {
    return null;
  }
};

export const streamDashboardChat = async (
  input: DashboardChatSendAction,
  handlers: {
    onChunk?: (chunkText: string) => void;
    /** D0: a real phase of the turn (« Recherche « tarif » »). */
    onPhase?: (label: string) => void;
  } = {}
): Promise<DashboardChatResponse> => {
  const accessToken = await getAccessToken();

  const response = await fetch(getDashboardChatWorkerUrl(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
      apikey: supabaseAnonKey,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await toDashboardChatError(response);
  }

  if (!response.body) {
    throw new Error('Réponse de streaming invalide du chat dashboard.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completedResponse: DashboardChatResponse | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    while (true) {
      const separatorIndex = buffer.indexOf('\n\n');
      if (separatorIndex === -1) {
        break;
      }

      const rawEvent = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const parsedEvent = parseSseEvent(rawEvent);
      if (!parsedEvent) continue;

      if (parsedEvent.event === 'assistant_message_chunk') {
        const data = parsedEvent.data as { text?: unknown };
        if (typeof data.text === 'string' && data.text.length > 0) {
          handlers.onChunk?.(data.text);
        }
        continue;
      }

      if (parsedEvent.event === 'assistant_phase') {
        const data = parsedEvent.data as { label?: unknown };
        if (typeof data.label === 'string' && data.label.length > 0) handlers.onPhase?.(data.label);
        continue;
      }

      if (parsedEvent.event === 'assistant_message_complete') {
        if (!isDashboardChatResponse(parsedEvent.data)) {
          throw new Error('Réponse finale invalide du chat dashboard.');
        }
        completedResponse = parsedEvent.data;
        continue;
      }

      if (parsedEvent.event === 'error') {
        const data = parsedEvent.data as { message?: unknown };
        throw new Error(
          typeof data.message === 'string'
            ? data.message
            : 'Le streaming du chat dashboard a échoué.'
        );
      }
    }
  }

  if (!completedResponse) {
    throw new Error('Le streaming du chat dashboard s’est terminé sans réponse finale.');
  }

  return completedResponse;
};
